// DemoBookingModal.jsx
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { Constant } from "../../helper/Constants";

/**
 * DemoBookingModal (fixed select visibility)
 */
const DemoBookingModal = forwardRef(function DemoBookingModal(_, ref) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });

  const [form, setForm] = useState({
    studentName: "",
    guardianName: "",
    email: "",
    whatsapp: "",
    city: "",
    ageGroup: "6 to 12 Years old",
    classDate: "",
  });

  useImperativeHandle(ref, () => ({
    open: (prefill = {}) => {
      setForm((p) => ({ ...p, ...prefill }));
      setStatusMsg({ text: "", type: "" });
      setShow(true);
    },
    close: () => setShow(false),
  }));

  useEffect(() => {
    const globalOpen = (prefill = {}) => {
      setForm((p) => ({ ...p, ...prefill }));
      setStatusMsg({ text: "", type: "" });
      setShow(true);
    };
    window.openDemoModal = globalOpen;
    return () => {
      try {
        if (window.openDemoModal === globalOpen) delete window.openDemoModal;
      } catch {}
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const validate = () => {
    if (!form.studentName || form.studentName.trim().length === 0) {
      setStatusMsg({ text: "Please enter student name.", type: "error" });
      return false;
    }
    if (!form.whatsapp || form.whatsapp.trim().length === 0) {
      setStatusMsg({ text: "Please enter WhatsApp number.", type: "error" });
      return false;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setStatusMsg({
        text: "Please enter a valid email address.",
        type: "error",
      });
      return false;
    }
    setStatusMsg({ text: "", type: "" });
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setStatusMsg({ text: "Submitting...", type: "info" });

    try {
      const payload = {
        student_name: form.studentName,
        guardian_name: form.guardianName,
        email: form.email,
        contact: form.whatsapp,
        city: form.city,
        age: form.ageGroup,
        date: form.classDate,
      };

      const endpoint =
        ( Constant.postUrls.createDemo) 

      const resp = await axios.post(endpoint, payload);

      if (resp?.data?.status === "success" || resp?.status === 200) {
        setStatusMsg({
          text: resp?.data?.message || "Demo booking submitted!",
          type: "success",
        });
        setTimeout(() => {
          setShow(false);
          setForm({
            studentName: "",
            guardianName: "",
            email: "",
            whatsapp: "",
            city: "",
            ageGroup: "6 to 12 Years old",
            classDate: "",
          });
          setStatusMsg({ text: "", type: "" });
        }, 900);
      } else {
        const msg =
          resp?.data?.message ||
          "Failed to submit demo booking. Please try again later.";
        setStatusMsg({ text: msg, type: "error" });
      }
    } catch (err) {
      console.error("Booking failed", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Booking failed. Please try again.";
      setStatusMsg({ text: msg, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    headerBar: {
      background: "#29166e",
      color: "#fff",
      padding: "16px 20px",
      textAlign: "center",
    },
    modalWrapper: {
      borderRadius: 12,
      overflow: "hidden",
      boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
    },
    body: { background: "#fff", padding: "20px 22px" },
    label: { fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#222" },
    input: {
      borderRadius: 10,
      padding: "12px 14px",
      fontSize: 14,
      border: "1px solid #e3e3e3",
      background: "#fbfbfb",
      transition: "box-shadow .15s, border-color .15s",
      boxSizing: "border-box",
      height: 44,
      lineHeight: "20px",
      color: "#111",
    },
    select: {
      borderRadius: 10,
      padding: "12px 44px 12px 14px", // leave space on right for arrow
      fontSize: 14,
      background: "#fbfbfb",
      border: "1px solid #e3e3e3",
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      boxSizing: "border-box",
      height: 44,
      lineHeight: "20px",
      color: "#111",
      cursor: "pointer",
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg fill='%2329166e' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "18px",
    },
    actionBtn: {
      background: "linear-gradient(180deg,#29166e,#1e0f54)",
      borderColor: "#29166e",
      width: "100%",
      fontWeight: 800,
      padding: "12px 16px",
      borderRadius: 10,
      marginTop: 6,
      fontSize: 16,
      color: "#fff",
    },
    note: { fontSize: 12, color: "#666", marginTop: 8, lineHeight: 1.4 },
    statusSuccess: {
      color: "#127a3b",
      fontSize: 13,
      marginTop: 10,
      textAlign: "center",
    },
    statusError: {
      color: "#b02a37",
      fontSize: 13,
      marginTop: 10,
      textAlign: "center",
    },
    statusInfo: {
      color: "#555",
      fontSize: 13,
      marginTop: 10,
      textAlign: "center",
    },
  };

  const inputProps = (name) => ({
    name,
    value: form[name],
    onChange: handleChange,
    style: styles.input,
  });

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      centered
      backdrop="static"
      keyboard={true}
      size="md"
      dialogClassName="demo-modal-dialog"
    >
      <div style={styles.modalWrapper}>
        <div style={styles.headerBar}>
          <button
            type="button"
            onClick={() => setShow(false)}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 8,
              right: 12,
              background: "transparent",
              border: "none",
              color: "red",
              fontSize: 26,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>

          <small style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
            Abacus Live Classes
          </small>
          <h4 style={{ margin: "6px 0 2px", color: "#fff" }}>
            Book A Free Demo Class
          </h4>
          <small style={{ color: "rgba(255,255,255,0.9)" }}>
            Age Group 6-12 years only
          </small>
        </div>

        <Form onSubmit={handleSubmit}>
          <div style={styles.body}>
            <Row className="gx-3">
              <Col xs={12}>
                <label style={styles.label} htmlFor="studentName">
                  Student Name
                </label>
                <Form.Control
                  id="studentName"
                  {...inputProps("studentName")}
                  placeholder="Enter Student Name"
                />
              </Col>

              <Col xs={12} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="guardianName">
                  Guardian's Name
                </label>
                <Form.Control
                  id="guardianName"
                  {...inputProps("guardianName")}
                  placeholder="Enter Guardian's Name"
                />
              </Col>

              <Col xs={12} md={6} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="email">
                  Email (optional)
                </label>
                <Form.Control
                  id="email"
                  type="email"
                  {...inputProps("email")}
                  placeholder="Enter Email"
                />
              </Col>

              <Col xs={12} md={6} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="whatsapp">
                  WhatsApp / Contact No.
                </label>
                <Form.Control
                  id="whatsapp"
                  {...inputProps("whatsapp")}
                  placeholder="Enter WhatsApp number"
                />
              </Col>

              <Col xs={12} md={6} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="city">
                  City
                </label>
                <Form.Control
                  id="city"
                  {...inputProps("city")}
                  placeholder="Enter Your City"
                />
              </Col>

              <Col xs={12} md={6} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="ageGroup">
                  Age Group
                </label>
                <Form.Control
                  as="select"
                  id="ageGroup"
                  name="ageGroup"
                  value={form.ageGroup}
                  onChange={handleChange}
                  style={styles.select}
                  className="custom-select-visible"
                >
                  <option value="6 to 12 Years old">6 to 12 Years old</option>
                  <option value="3 to 5 Years old">3 to 5 Years old</option>
                  <option value="13+ Years old">13+ Years old</option>
                </Form.Control>
              </Col>

              <Col xs={12} md={6} style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="classDate">
                  Preferred Date
                </label>
                <Form.Control
                  id="classDate"
                  name="classDate"
                  type="date"
                  value={form.classDate}
                  onChange={handleChange}
                  style={styles.input}
                />
              </Col>

              <Col xs={12}>
                <div style={styles.note}>
                  By registering here, I agree to VASA-Abacus{" "}
                  <a href="/terms" target="_blank" rel="noreferrer">
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                </div>
              </Col>

              <Col xs={12}>
                {statusMsg.text && (
                  <div
                    role="status"
                    style={
                      statusMsg.type === "success"
                        ? styles.statusSuccess
                        : statusMsg.type === "error"
                        ? styles.statusError
                        : styles.statusInfo
                    }
                  >
                    {statusMsg.text}
                  </div>
                )}
              </Col>

              <Col xs={12} style={{ marginTop: 14 }}>
                <Button
                  type="submit"
                  disabled={loading}
                  style={styles.actionBtn}
                  aria-label="Book Now"
                >
                  {loading ? "Booking..." : "Book Now"}
                </Button>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <style>{`
        .custom-select-visible option {
          background: #fff !important;
          color: #111 !important;
          padding: 8px;
          font-size: 14px;
        }
        .demo-modal-dialog .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 4px rgba(41,22,110,0.08) !important;
          border-color: #29166e !important;
          background: #fff !important;
        }
        /* ensure the select shows its text on iOS / Safari where select inherits some styles */
        .custom-select-visible {
          color: #111 !important;
        }
      `}</style>
    </Modal>
  );
});

export default DemoBookingModal;
